import React, { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import "./EcommerceQuickOrder.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import { useCart } from "@contexts/CartContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import { ICartItemToAdd } from "@models/DTO/IBulkAddItemsToCartDTO";
import IQuickOrderRowUI from "@models/IQuickOrderRowUI";
import IOrder from "@models/IOrder";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import CartService from "@services/CartService";
import HelperService from "@services/HelperService";
import OrderEntryService from "@services/OrderEntryService";
import UserService from "@services/UserService";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwP } from "../../shared/OwP/OwP";

type Props = {
    toggleCartSidebar: () => void;
};

// Echoed back on the typeahead response so this view and the card/list views
// (which share the same socket event) each consume only their own results.
const QUICK_ORDER_STATE = "quickOrder";
const MAX_ROWS = 100;
// Per-line qty ceiling: keeps runaway typed values well inside safe-integer range
// so extended-price math and the bulk-add payload stay exact.
const MAX_QTY = 999999;

export const EcommerceQuickOrder: React.FC<Props> = (props) => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();
    const { refreshCart } = useCart();

    const [rows, setRows] = useState<IQuickOrderRowUI[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const footerRef = useRef<HTMLDivElement | null>(null);

    // The footer is position: fixed (the app's nested scroll containers defeat sticky),
    // so it must be aligned to the grid container's box — otherwise a viewport-wide bar
    // runs under the side menus. The sidebars animate open/closed, so a one-shot
    // measurement goes stale: instead, each animation frame nudges the footer by the
    // difference between where it is and where the container is. Styles are written
    // directly (not via state) to avoid a re-render per frame.
    useEffect(() => {
        const container = containerRef.current;
        const footer = footerRef.current;

        if (!container || !footer) {
            return;
        }

        let frameId: number;

        const track = () => {
            const containerRect = container.getBoundingClientRect();
            const footerRect = footer.getBoundingClientRect();

            const leftDelta = containerRect.left - footerRect.left;
            const widthDelta = containerRect.width - footerRect.width;

            if (Math.abs(leftDelta) >= 0.5 || Math.abs(widthDelta) >= 0.5) {
                footer.style.left = `${(parseFloat(footer.style.left) || 0) + leftDelta}px`;
                footer.style.width = `${(parseFloat(footer.style.width) || footerRect.width) + widthDelta}px`;
            }

            frameId = requestAnimationFrame(track);
        };

        track();

        return () => cancelAnimationFrame(frameId);
    }, [isLoading, rows.length]);

    const supplierVendorToken = UserService.getSupplierVendorToken(vendorModel, supplierModel.id);

    // Suppliers may allow fractional quantities (qty_precision 1-3, matching the
    // card/list views); 0 or unset keeps the original integer-only entry.
    const qtyPrecision = supplierModel.qty_precision || 0;

    const sanitizeQtyInput = (raw: string): string => {
        if (qtyPrecision <= 0) {
            // Integer-only: ignore anything typed after a decimal point rather than
            // collapsing "1.5" into 15.
            return raw
                .split(".")[0]
                .replace(/\D/g, "")
                .replace(/^0+(?=\d)/, "");
        }

        // Keep digits and the first decimal point; excess decimals are truncated to
        // the supplier's precision rather than rounded so typing never jumps.
        const [whole, ...decimals] = raw.replace(/[^\d.]/g, "").split(".");
        const cleanWhole = whole.replace(/^0+(?=\d)/, "");

        if (!decimals.length) {
            return cleanWhole;
        }

        return `${cleanWhole}.${decimals.join("").slice(0, qtyPrecision)}`;
    };

    // In-progress quantities autosave to localStorage so an abandoned tab / dead session
    // doesn't lose a half-keyed order. Cleared once the lines land in the cart.
    const draftStorageKey = `ow_quick_order_draft:${supplierModel.guid}:${vendorModel.guid}:${userModel.guid}`;

    const readDraftQtys = (): Record<string, number> => {
        try {
            const raw = localStorage.getItem(draftStorageKey);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };

    const onGetTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        if (data.state !== QUICK_ORDER_STATE) {
            return;
        }

        const draftQtys = readDraftQtys();

        // Same pricing inputs the card/list views use (EcommerceListView).
        const pricingOrder = {
            Price_Group: vendorModel.Price_Group,
            supplierVendorToken,
        } as IOrder;

        const nextRows: IQuickOrderRowUI[] = data.catalog_items
            // Product groups and advanced kits need option/builder modals the grid can't host.
            .filter((item) => !item.is_product_group && !(item.is_kit && item.is_advanced_kit))
            .slice(0, MAX_ROWS)
            .map((item) => {
                const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(item, supplierModel, pricingOrder);
                const draftQty = draftQtys[item.guid] || 0;

                return {
                    guid: item.guid,
                    catalogItem: item,
                    sku: item.sku || "",
                    name: item.name,
                    unitName: item.Unit_Of_Measurement?.name?.toLowerCase() || "",
                    unitPrice: priceDetail.discountedPrice,
                    discountId: priceDetail.discount?.id,
                    qty: draftQty,
                    qtyText: draftQty ? String(draftQty) : "",
                };
            });

        setRows(nextRows);
        setIsLoading(false);
    };

    const getQuickOrderItems = () => {
        if (!vendorModel.Catalog) {
            setIsLoading(false);
            return;
        }

        // Same fetch that populates the portal home view: common items, falling back to
        // standard items — and template items when the supplier has template_home_items
        // enabled and the customer has a template (handled server-side).
        const payload: ITypeaheadCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid: vendorModel.Catalog.guid,
            name: "",
            limit: MAX_ROWS,
            offset: 0,
            onlyActive: true,
            includeKitItems: true,
            priceGroupGuid: vendorModel.Price_Group?.guid,
            onlyCommonItems: true,
            includeStandardIfNoCommonItems: true,
            state: QUICK_ORDER_STATE,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        setIsLoading(true);
        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onGetTypeaheadCatalogItem);

        getQuickOrderItems();

        return () => {
            socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onGetTypeaheadCatalogItem);
        };
    }, []);

    // Persist the draft on every change (covers typing, submit clearing, and partial
    // failures — whatever remains keyed in is what's saved). An empty rows array means
    // nothing loaded, so leave any existing draft alone.
    useEffect(() => {
        if (isLoading || !rows.length) {
            return;
        }

        const draftQtys: Record<string, number> = {};

        for (const row of rows) {
            if (row.qty > 0) {
                draftQtys[row.guid] = row.qty;
            }
        }

        try {
            if (Object.keys(draftQtys).length) {
                localStorage.setItem(draftStorageKey, JSON.stringify(draftQtys));
            } else {
                localStorage.removeItem(draftStorageKey);
            }
        } catch {
            // Storage unavailable (private mode / quota) — autosave is best-effort.
        }
    }, [rows, isLoading]);

    const handleQtyChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        let qtyText = sanitizeQtyInput(e.target.value);
        // A lone "." parses to NaN — treat in-progress entry as 0 until digits land.
        let qty = qtyText === "" ? 0 : Number(qtyText) || 0;

        if (qty > MAX_QTY) {
            qty = MAX_QTY;
            qtyText = String(MAX_QTY);
        }

        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, qty, qtyText, error: undefined } : row)));
    };

    const handleQtyKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case "ArrowDown":
            case "Enter":
                e.preventDefault();
                inputRefs.current[index + 1]?.focus();
                break;
            case "ArrowUp":
                e.preventDefault();
                inputRefs.current[index - 1]?.focus();
                break;
            default:
                break;
        }
    };

    const formatPrice = (value: number) => {
        return HelperService.formatCurrency({
            value,
            precision: supplierModel.pricing_precision || 2,
            currency: "USD",
        });
    };

    const totalUnits = rows.reduce((sum, row) => sum + row.qty, 0);
    const lineCount = rows.filter((row) => row.qty > 0).length;
    const orderTotal = rows.reduce((sum, row) => sum + row.qty * row.unitPrice, 0);

    const submitQuickOrder = async () => {
        const linesToAdd = rows.filter((row) => row.qty > 0);

        if (!linesToAdd.length || isSubmitting) {
            return;
        }

        const items: ICartItemToAdd[] = linesToAdd.map((row) => {
            const item: ICartItemToAdd = {
                catalogItemGuid: row.guid,
                qty: row.qty,
            };

            if (supplierModel.has_discounts && row.discountId) {
                item.discountId = row.discountId;
            }

            return item;
        });

        setIsSubmitting(true);
        uiContext.setActivityIndicator(true);

        try {
            const response = await CartService.bulkAddItemsToCart(
                items,
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );

            await refreshCart();

            const failuresByGuid = new Map(
                response.results.filter((result) => !result.success).map((result) => [result.catalogItemGuid, result])
            );

            if (failuresByGuid.size === 0) {
                // Full success: clear the grid for the next round of entry.
                setRows((prev) => prev.map((row) => ({ ...row, qty: 0, qtyText: "", error: undefined })));

                uiContext.notifyFn(null, {
                    message: "quick_order_items_added",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                } as INotification);

                // Match the single-item add-to-cart desktop behavior (Bootstrap md breakpoint).
                if (window.innerWidth >= 768) {
                    props.toggleCartSidebar();
                }
            } else {
                // Clear only the lines that made it into the cart; keep failed lines keyed
                // in with their error so the user can adjust and resubmit.
                setRows((prev) =>
                    prev.map((row) => {
                        if (row.qty <= 0) {
                            return row;
                        }

                        const failure = failuresByGuid.get(row.guid);

                        if (failure) {
                            return { ...row, error: failure.message || "" };
                        }

                        return { ...row, qty: 0, qtyText: "", error: undefined };
                    })
                );

                uiContext.notifyFn(null, {
                    message: failuresByGuid.size < items.length ? "quick_order_partial_success" : "quick_order_failed",
                    level: NotificationLevelEnum.warning,
                    position: NotificationPositionEnum.bc,
                } as INotification);
            }
        } catch (error) {
            uiContext.notifyFn({
                eid: "",
                title: "error",
                message: error instanceof Error ? error.message : "quick_order_failed",
            });
        } finally {
            setIsSubmitting(false);
            uiContext.setActivityIndicator(false);
        }
    };

    if (isLoading) {
        return (
            <div className={"EcommerceQuickOrder d-flex justify-content-center align-items-center py-5"}>
                <ActivityIndicator active={true} className={"me-2"} />
                <OwLabel title={"loading"} noBottomMargin={true} />
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className={"EcommerceQuickOrder text-center py-5"}>
                <OwP content={"quick_order_no_items"} />
            </div>
        );
    }

    return (
        <div className={"EcommerceQuickOrder"} ref={containerRef}>
            <div className={"d-flex justify-content-between align-items-baseline flex-wrap"}>
                <OwLabel title={"quick_order"} isBold={true} />
                <div className={"quick-order-hint text-muted"}>
                    <i className={"fad fa-arrow-up me-1"} />
                    <i className={"fad fa-arrow-down me-1"} />
                    <OwLabel title={"quick_order_keyboard_hint"} noBottomMargin={true} />
                </div>
            </div>
            <table className={"quick-order-table table align-middle"}>
                <thead>
                    <tr>
                        {/* On phones the SKU folds into the Product cell and Unit/Ext. Price hide —
                            three columns (Product, Price, Qty) fit the viewport; the footer total
                            still updates live in place of the per-row extended price. */}
                        <th className={"d-none d-md-table-cell"}>
                            <OwLabel title={"sku"} noBottomMargin={true} />
                        </th>
                        <th>
                            <OwLabel title={"product"} noBottomMargin={true} />
                        </th>
                        <th className={"d-none d-md-table-cell"}>
                            <OwLabel title={"unit"} noBottomMargin={true} />
                        </th>
                        <th className={"text-end"}>
                            <OwLabel title={"price"} noBottomMargin={true} />
                        </th>
                        <th className={"text-center"}>
                            <OwLabel title={"qty"} noBottomMargin={true} />
                        </th>
                        <th className={"d-none d-md-table-cell text-end"}>
                            <OwLabel title={"extended_price"} noBottomMargin={true} />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={row.guid}>
                            <td className={"d-none d-md-table-cell text-small"}>{row.sku}</td>
                            <td>
                                {row.name}
                                <div className={"d-md-none text-small text-muted"}>{row.sku}</div>
                                {row.error ? <div className={"text-danger text-small"}>{row.error}</div> : null}
                            </td>
                            <td className={"d-none d-md-table-cell"}>{row.unitName}</td>
                            <td className={"text-end"}>{formatPrice(row.unitPrice)}</td>
                            <td className={"text-center"}>
                                <input
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    className={"quick-order-qty form-control form-control-sm " + (row.error ? "is-invalid" : "")}
                                    type={"text"}
                                    inputMode={qtyPrecision > 0 ? "decimal" : "numeric"}
                                    pattern={qtyPrecision > 0 ? undefined : "[0-9]*"}
                                    autoComplete={"off"}
                                    aria-label={row.name}
                                    value={row.qtyText}
                                    onChange={(e) => handleQtyChange(index, e)}
                                    onKeyDown={(e) => handleQtyKeyDown(index, e)}
                                    onFocus={(e) => e.target.select()}
                                />
                            </td>
                            <td className={"d-none d-md-table-cell text-end"}>
                                {row.qty > 0 ? formatPrice(row.qty * row.unitPrice) : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className={"quick-order-footer"} ref={footerRef}>
                <div>
                    <OwLabel title={"total_units"} noBottomMargin={true} isBold={true} />
                    <div>{totalUnits}</div>
                </div>
                <div>
                    <OwLabel title={"lines"} noBottomMargin={true} isBold={true} />
                    <div>{lineCount}</div>
                </div>
                <div>
                    <OwLabel title={"total"} noBottomMargin={true} isBold={true} />
                    <div>{formatPrice(orderTotal)}</div>
                </div>
                <OwButton
                    className={"btn-primary"}
                    title={"add_all_to_cart"}
                    disabled={lineCount === 0 || isSubmitting}
                    fn={submitQuickOrder}
                />
            </div>
        </div>
    );
};

export default EcommerceQuickOrder;
